import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw, FiCircle, FiSquare, FiTriangle } = FiIcons;

function ShapeGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [questionsCompleted, setQuestionsCompleted] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [gameMode, setGameMode] = useState('2d'); // '2d' or '3d'

  // 2D Shapes for younger kids (5-7)
  const shapes2D = [
    { name: 'circle', component: 'circle', emoji: '⭕', color: '#3b82f6' },
    { name: 'square', component: 'square', emoji: '⬜', color: '#ef4444' },
    { name: 'triangle', component: 'triangle', emoji: '🔺', color: '#22c55e' },
    { name: 'rectangle', component: 'rectangle', emoji: '📱', color: '#a855f7' },
    { name: 'star', component: 'star', emoji: '⭐', color: '#eab308' },
    { name: 'heart', component: 'heart', emoji: '❤️', color: '#ec4899' }
  ];

  // 3D Shapes for older kids (8+)
  const shapes3D = [
    { name: 'cube', component: 'cube', emoji: '🧊', color: '#3b82f6', faces: 6, edges: 12, vertices: 8 },
    { name: 'sphere', component: 'sphere', emoji: '⚽', color: '#22c55e', faces: 1, edges: 0, vertices: 0 },
    { name: 'cylinder', component: 'cylinder', emoji: '🥫', color: '#ef4444', faces: 3, edges: 2, vertices: 0 },
    { name: 'cone', component: 'cone', emoji: '🍦', color: '#f97316', faces: 2, edges: 1, vertices: 1 },
    { name: 'pyramid', component: 'pyramid', emoji: '🔺', color: '#a855f7', faces: 5, edges: 8, vertices: 5 },
    { name: 'rectangular prism', component: 'prism', emoji: '📦', color: '#06b6d4', faces: 6, edges: 12, vertices: 8 }
  ];

  const ShapeComponent = ({ shape, size = 80, is3D = false }) => {
    const baseClass = `transition-all duration-200`;
    
    if (is3D) {
      // 3D Shape representations with CSS
      switch (shape.component) {
        case 'cube':
          return (
            <div className="relative" style={{ width: size, height: size }}>
              <div 
                className={`${baseClass} border-4 border-white shadow-lg relative`}
                style={{ 
                  width: size * 0.8, 
                  height: size * 0.8, 
                  backgroundColor: shape.color,
                  transform: 'rotateX(15deg) rotateY(15deg)'
                }}
              >
                {/* Top face */}
                <div 
                  className="absolute border-2 border-white"
                  style={{
                    width: size * 0.8,
                    height: size * 0.2,
                    backgroundColor: `${shape.color}dd`,
                    top: -size * 0.15,
                    left: size * 0.15,
                    transform: 'rotateX(90deg) translateZ(10px)'
                  }}
                />
                {/* Right face */}
                <div 
                  className="absolute border-2 border-white"
                  style={{
                    width: size * 0.2,
                    height: size * 0.8,
                    backgroundColor: `${shape.color}bb`,
                    top: 0,
                    right: -size * 0.15,
                    transform: 'rotateY(90deg) translateZ(10px)'
                  }}
                />
              </div>
            </div>
          );
        case 'sphere':
          return (
            <div 
              className={`${baseClass} rounded-full border-4 border-white shadow-lg relative`}
              style={{ 
                width: size, 
                height: size, 
                backgroundColor: shape.color,
                background: `radial-gradient(circle at 30% 30%, ${shape.color}ff, ${shape.color}aa)`
              }}
            >
              {/* Highlight for 3D effect */}
              <div 
                className="absolute rounded-full bg-white opacity-30"
                style={{
                  width: size * 0.3,
                  height: size * 0.3,
                  top: size * 0.1,
                  left: size * 0.2
                }}
              />
            </div>
          );
        case 'cylinder':
          return (
            <div className="relative" style={{ width: size, height: size }}>
              {/* Top ellipse */}
              <div 
                className="absolute border-2 border-white"
                style={{
                  width: size * 0.8,
                  height: size * 0.2,
                  backgroundColor: `${shape.color}dd`,
                  borderRadius: '50%',
                  top: 0,
                  left: size * 0.1
                }}
              />
              {/* Body */}
              <div 
                className={`${baseClass} border-4 border-white shadow-lg`}
                style={{ 
                  width: size * 0.8, 
                  height: size * 0.7, 
                  backgroundColor: shape.color,
                  marginLeft: size * 0.1,
                  marginTop: size * 0.1
                }}
              />
              {/* Bottom ellipse */}
              <div 
                className="absolute border-2 border-white"
                style={{
                  width: size * 0.8,
                  height: size * 0.2,
                  backgroundColor: `${shape.color}bb`,
                  borderRadius: '50%',
                  bottom: 0,
                  left: size * 0.1
                }}
              />
            </div>
          );
        case 'cone':
          return (
            <div className="relative" style={{ width: size, height: size }}>
              {/* Cone body */}
              <div 
                className={`${baseClass} border-4 border-white shadow-lg`}
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: `${size * 0.4}px solid transparent`,
                  borderRight: `${size * 0.4}px solid transparent`,
                  borderBottom: `${size * 0.7}px solid ${shape.color}`,
                  marginLeft: size * 0.1,
                  marginTop: size * 0.1
                }}
              />
              {/* Base */}
              <div 
                className="absolute border-2 border-white"
                style={{
                  width: size * 0.8,
                  height: size * 0.15,
                  backgroundColor: `${shape.color}bb`,
                  borderRadius: '50%',
                  bottom: 0,
                  left: size * 0.1
                }}
              />
            </div>
          );
        case 'pyramid':
          return (
            <div className="relative" style={{ width: size, height: size }}>
              {/* Front face */}
              <div 
                className={`${baseClass} border-4 border-white shadow-lg`}
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: `${size * 0.4}px solid transparent`,
                  borderRight: `${size * 0.4}px solid transparent`,
                  borderBottom: `${size * 0.6}px solid ${shape.color}`,
                  marginLeft: size * 0.1,
                  marginTop: size * 0.2
                }}
              />
              {/* Right face */}
              <div 
                className="absolute border-2 border-white"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: `${size * 0.3}px solid transparent`,
                  borderBottom: `${size * 0.6}px solid ${shape.color}bb`,
                  right: size * 0.1,
                  top: size * 0.2
                }}
              />
              {/* Base */}
              <div 
                className="absolute border-2 border-white"
                style={{
                  width: size * 0.8,
                  height: size * 0.8,
                  backgroundColor: `${shape.color}88`,
                  bottom: 0,
                  left: size * 0.1,
                  transform: 'rotateX(90deg) translateZ(-10px)'
                }}
              />
            </div>
          );
        case 'prism':
          return (
            <div className="relative" style={{ width: size, height: size }}>
              {/* Front face */}
              <div 
                className={`${baseClass} border-4 border-white shadow-lg`}
                style={{ 
                  width: size * 0.6, 
                  height: size * 0.8, 
                  backgroundColor: shape.color,
                  transform: 'rotateY(10deg)'
                }}
              />
              {/* Top face */}
              <div 
                className="absolute border-2 border-white"
                style={{
                  width: size * 0.6,
                  height: size * 0.15,
                  backgroundColor: `${shape.color}dd`,
                  top: 0,
                  left: size * 0.1,
                  transform: 'rotateX(45deg) translateZ(5px)'
                }}
              />
              {/* Right face */}
              <div 
                className="absolute border-2 border-white"
                style={{
                  width: size * 0.15,
                  height: size * 0.8,
                  backgroundColor: `${shape.color}bb`,
                  top: 0,
                  right: size * 0.1,
                  transform: 'rotateY(45deg) translateZ(5px)'
                }}
              />
            </div>
          );
        default:
          return <div className="text-4xl">{shape.emoji}</div>;
      }
    } else {
      // 2D Shapes (original logic)
      switch (shape.component) {
        case 'circle':
          return (
            <div 
              className={`${baseClass} rounded-full border-4 border-white shadow-lg`}
              style={{ width: size, height: size, backgroundColor: shape.color }}
            />
          );
        case 'square':
          return (
            <div 
              className={`${baseClass} border-4 border-white shadow-lg`}
              style={{ width: size, height: size, backgroundColor: shape.color }}
            />
          );
        case 'triangle':
          return (
            <div 
              className={`${baseClass} border-4 border-white shadow-lg`}
              style={{
                width: 0,
                height: 0,
                borderLeft: `${size/2}px solid transparent`,
                borderRight: `${size/2}px solid transparent`,
                borderBottom: `${size}px solid ${shape.color}`,
                borderTop: 'none'
              }}
            />
          );
        case 'rectangle':
          return (
            <div 
              className={`${baseClass} border-4 border-white shadow-lg`}
              style={{ width: size * 1.5, height: size, backgroundColor: shape.color }}
            />
          );
        case 'star':
          return <div className="text-6xl filter drop-shadow-lg">{shape.emoji}</div>;
        case 'heart':
          return <div className="text-6xl filter drop-shadow-lg">{shape.emoji}</div>;
        default:
          return <div>{shape.emoji}</div>;
      }
    }
  };

  const generateQuestion = () => {
    const use3D = state.age >= 8 && gameMode === '3d';
    const availableShapes = use3D ? shapes3D : (state.age <= 7 ? shapes2D.slice(0, 4) : shapes2D);
    const targetShape = availableShapes[Math.floor(Math.random() * availableShapes.length)];

    // Generate wrong answers
    const wrongAnswers = [];
    while (wrongAnswers.length < 3) {
      const wrong = availableShapes[Math.floor(Math.random() * availableShapes.length)];
      if (wrong.name !== targetShape.name && !wrongAnswers.some(w => w.name === wrong.name)) {
        wrongAnswers.push(wrong);
      }
    }

    const allAnswers = [targetShape, ...wrongAnswers].sort(() => Math.random() - 0.5);

    return {
      targetShape,
      answers: allAnswers,
      correctAnswer: targetShape.name,
      is3D: use3D
    };
  };

  useEffect(() => {
    // Automatically set game mode based on age
    if (state.age >= 8) {
      setGameMode('3d');
    } else {
      setGameMode('2d');
    }
    setCurrentQuestion(generateQuestion());
  }, [state.age, gameMode]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
    setShowResult(true);

    const isCorrect = answer === currentQuestion.correctAnswer;
    if (isCorrect) {
      const points = gameMode === '3d' ? 15 : 10; // More points for 3D shapes
      dispatch({ type: 'ADD_SCORE', payload: points });
    } else {
      dispatch({ type: 'ADD_WRONG_ANSWER' });
    }

    setTimeout(() => {
      const newCompleted = questionsCompleted + 1;
      setQuestionsCompleted(newCompleted);

      if (newCompleted >= 5) {
        setGameCompleted(true);
        dispatch({ type: 'GAME_COMPLETED' });
      } else {
        setCurrentQuestion(generateQuestion());
        setSelectedAnswer(null);
        setShowResult(false);
      }
    }, 2000);
  };

  const restartGame = () => {
    setCurrentQuestion(generateQuestion());
    setSelectedAnswer(null);
    setShowResult(false);
    setQuestionsCompleted(0);
    setGameCompleted(false);
  };

  const toggleGameMode = () => {
    if (state.age >= 8) {
      const newMode = gameMode === '2d' ? '3d' : '2d';
      setGameMode(newMode);
      setCurrentQuestion(null);
      setTimeout(() => {
        setCurrentQuestion(generateQuestion());
      }, 100);
    }
  };

  if (!currentQuestion && !gameCompleted) {
    return <div>Loading...</div>;
  }

  if (gameCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-safari-200 text-center max-w-md">
          <div className="text-6xl mb-4">🔷</div>
          <h2 className="text-3xl font-kid text-jungle-700 mb-4">
            Shape Master!
          </h2>
          <p className="font-comic text-jungle-600 mb-6">
            You completed the {gameMode === '3d' ? '3D ' : ''}Shape Adventure! 
            You're excellent at recognizing {gameMode === '3d' ? '3D ' : ''}shapes!
          </p>
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={restartGame}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Play Again
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-safari-500 to-safari-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Back to Games
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen p-4"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiArrowLeft} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Back</span>
          </button>

          <div className="text-center">
            <h1 className="text-3xl font-kid text-jungle-700">
              {gameMode === '3d' ? '3D ' : ''}Shape Adventure 🔷
            </h1>
            {state.age >= 8 && (
              <button
                onClick={toggleGameMode}
                className="font-comic text-safari-600 hover:text-safari-700 transition-colors text-sm"
              >
                Switch to {gameMode === '2d' ? '3D' : '2D'} Shapes
              </button>
            )}
          </div>

          <button
            onClick={restartGame}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Restart</span>
          </button>
        </motion.div>

        {/* Progress */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${(questionsCompleted / 5) * 100}%` }}
          className="h-3 bg-blue-400 rounded-full mb-8 shadow-inner"
        >
          <div className="w-full h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"></div>
        </motion.div>

        {/* Question */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-safari-200 mb-8"
        >
          <h2 className="text-2xl font-kid text-center text-jungle-700 mb-6">
            What {gameMode === '3d' ? '3D ' : ''}shape is this?
          </h2>

          {/* Shape Display */}
          <div className="flex justify-center mb-8">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              className="relative flex items-center justify-center"
              style={{ minHeight: 120 }}
            >
              <ShapeComponent shape={currentQuestion.targetShape} size={100} is3D={currentQuestion.is3D} />
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                className="absolute -inset-8 rounded-full border-4 border-dashed border-safari-400 opacity-30"
              ></motion.div>
            </motion.div>
          </div>

          {/* 3D Shape Properties (for Grade 3+) */}
          {gameMode === '3d' && currentQuestion.targetShape.faces !== undefined && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="bg-jungle-100 rounded-2xl p-4 mb-6 text-center"
            >
              <h3 className="font-kid text-jungle-700 mb-2">Fun Facts:</h3>
              <div className="grid grid-cols-3 gap-4 text-sm font-comic text-jungle-600">
                <div>
                  <div className="text-lg">📐</div>
                  <div>Faces: {currentQuestion.targetShape.faces}</div>
                </div>
                <div>
                  <div className="text-lg">📏</div>
                  <div>Edges: {currentQuestion.targetShape.edges}</div>
                </div>
                <div>
                  <div className="text-lg">📍</div>
                  <div>Vertices: {currentQuestion.targetShape.vertices}</div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Answer Options */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {currentQuestion.answers.map((answer) => (
              <motion.button
                key={answer.name}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => !showResult && handleAnswerSelect(answer.name)}
                disabled={showResult}
                className={`p-6 rounded-2xl font-kid text-lg shadow-lg transition-all duration-200 ${
                  showResult
                    ? answer.name === currentQuestion.correctAnswer
                      ? 'bg-green-400 text-white'
                      : answer.name === selectedAnswer
                      ? 'bg-red-400 text-white'
                      : 'bg-gray-200 text-gray-500'
                    : 'bg-gradient-to-br from-safari-200 to-safari-300 text-jungle-700 hover:from-safari-300 hover:to-safari-400'
                }`}
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="flex items-center justify-center" style={{ minHeight: 40 }}>
                    <ShapeComponent shape={answer} size={32} is3D={currentQuestion.is3D} />
                  </div>
                  <span className="capitalize">{answer.name}</span>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Result Message */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-6 text-center"
              >
                {selectedAnswer === currentQuestion.correctAnswer ? (
                  <div className="text-green-600 font-kid text-xl">
                    🎉 Excellent! That's a {currentQuestion.targetShape.name}!
                  </div>
                ) : (
                  <div className="text-red-600 font-kid text-xl">
                    Good try! This shape is a {currentQuestion.targetShape.name}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default ShapeGame;